import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Product, apiService } from '../services/api';
import QuickAddToCartModal from './QuickAddToCartModal';

const BestSellers: React.FC = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddProduct, setQuickAddProduct] = useState<Product | null>(null);

  useEffect(() => {
    const fetchBestSellers = async () => {
      try {
        setLoading(true);
        const bestSellers = await apiService.getBestSellerProducts();
        // Ensure we always have an array and limit to 4 products
        const productsArray = Array.isArray(bestSellers) ? bestSellers : [];
        setProducts(productsArray.slice(0, 4));
        setError(null);
      } catch (err) {
        setError('Failed to load best seller products');
        setProducts([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchBestSellers();
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price) + ' Ks';
  };

  const getProductImage = (product: Product): string => {
    return product.product_images?.[0] || 'https://via.placeholder.com/400x400?text=No+Image';
  };

  const getProductHoverImage = (product: Product): string | undefined => {
    return product.hover_image || product.product_images?.[1];
  };

  const getProductBadge = (product: Product) => {
    // Always show BEST badge in Best Sellers section
    return { text: 'BEST', color: 'bg-blue-600' };
  };

  // Hide section entirely if loading, error, or no products
  if (loading || error || products.length === 0) {
    return null;
  }

  return (
    <>
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block">
            <span className="text-sm font-medium text-gray-500 tracking-wider uppercase mb-2 block">
              Signature Style
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Best Seller</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">See What Everyone's Buying</p>
          </div>
        </div>

        {/* Best Seller Products Grid */}
        <div className="mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {products.map((product) => {
              const badge = getProductBadge(product);
              const primaryImage = getProductImage(product);
              const hoverImage = getProductHoverImage(product) || primaryImage;
              
              return (
                <div
                  key={product.id}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="relative overflow-hidden">
                    <Link to={`/products/${product.slug}`} className="block">
                      {/* Product Image */}
                      <img
                        loading="lazy"
                        width="400"
                        height="400"
                        src={primaryImage}
                        alt={product.name}
                        className="w-full h-48 sm:h-80 object-cover transition-opacity duration-500 group-hover:opacity-0"
                      />
                      <img
                        loading="lazy"
                        width="400"
                        height="400"
                        src={hoverImage}
                        alt={`${product.name} hover view`}
                        className="absolute inset-0 w-full h-48 sm:h-80 object-cover transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                      />
                      
                      {/* Badge */}
                      <div className="absolute top-2 sm:top-4 left-2 sm:left-4">
                        <span className={`${badge.color} text-white text-xs px-2 py-0.5 sm:py-1 rounded font-medium`}>
                          {badge.text}
                        </span>
                      </div>
                      
                      {/* Discount Badge - Top Right */}
                      {(product.has_promotion || (product.promotional_price && product.promotional_price < product.price) || (product.discounted_price && product.discounted_price < product.price)) && product.discount_percentage && (
                        <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                          <span className="bg-orange-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 text-[10px] sm:text-xs font-semibold rounded shadow">
                            -{Math.round(product.discount_percentage)}%
                          </span>
                        </div>
                      )}
                    </Link>

                    {/* Action Buttons - Show on Hover */}
                    <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                    {/* Add to Cart */}
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (product.variants && product.variants.length > 0) {
                          setQuickAddProduct(product);
                          setQuickAddOpen(true);
                        } else {
                          addToCart(product);
                          const btn = e.currentTarget;
                          btn.classList.add('animate-bounce');
                          setTimeout(() => btn.classList.remove('animate-bounce'), 500);
                        }
                      }}
                      className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
                    >
                      <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6m0 0h12.5M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z" />
                      </svg>
                    </button>
                    

                    {/* Quick View */}
                    <button 
                      onClick={() => navigate(`/products/${product.slug}`)}
                      className="w-8 h-8 sm:w-12 sm:h-12 bg-gray-600 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors shadow-lg"
                    >
                      <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-3 sm:p-4">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-800 mb-2 line-clamp-2">
                      {product.name}
                    </h3>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      {product.has_promotion && product.discounted_price ? (
                        <>
                          <span className="text-xs sm:text-sm text-gray-500 line-through">
                            {formatPrice(product.price)}
                          </span>
                          <span className="text-sm sm:text-lg font-bold text-gray-900">
                            {formatPrice(product.discounted_price)}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm sm:text-lg font-bold text-gray-900">
                          {formatPrice(product.price)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* View All Button */}
        <div className="text-center">
          <button
            onClick={() => navigate('/product-signature')}
            className="inline-flex items-center justify-center bg-gray-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            View All Best Sellers
          </button>
        </div>
      </div>
    </section>

    {/* Quick Add To Cart Modal */}
    <QuickAddToCartModal
      isOpen={quickAddOpen}
      product={quickAddProduct}
      onClose={() => { setQuickAddOpen(false); setQuickAddProduct(null); }}
      onConfirm={(sel) => {
        if (!quickAddProduct) return;
        addToCart(quickAddProduct, sel.quantity, {
          variantId: sel.variantId,
          sizeName: sel.sizeName,
          unitPrice: sel.unitPrice,
          compareAtPrice: sel.compareAtPrice,
          inStock: sel.inStock,
          colorName: sel.colorName,
          colorCode: sel.colorCode,
        });
        setQuickAddOpen(false);
        setQuickAddProduct(null);
      }}
    />
    </>
  );
};

export default BestSellers;
